import {
  DataGrid,
  EmptyFieldLabel,
  ErrorLabel,
  FieldLabel,
  RequiredIndicator,
  useScreenSize,
} from '@myosh/odin-components';
import type {
  ContinuousScrollerSort,
  DataGridSettings,
  DynamicFieldComponentProps,
  JsonData,
  JsonDataItem,
  RowTemplateType,
} from '@myosh/odin-components';
import { useEffect, useMemo, useState } from 'react';
import type { ExtendedDynamicFormWorkflowStep } from '../../../@types/forms';
import type { RecordLinkProps } from '../../../@types/linked-records-field';
import type { RecordResult } from '../../../@types/records';
import useActiveRecordRef from '../../../hooks/use-active-record-ref';
import useFetchReverseLinkedRecords from '../../../hooks/use-fetch-reverse-linked-records';
import useFormPermissions from '../../../hooks/use-form-permissions';
import useFetchWorkflowSteps from '../../../hooks/use-workflow-steps';
import { useModulesQuery } from '../../../redux/services/module';
import { extractLinkedRecordsPermissions } from '../../../utils/form-permissions-util';
import { forceAssert } from '../../../utils/type-guards';
import {
  customLinkedRecordsComponents,
  defaultLinkedRecordGridSetting,
} from '../../dynamic-page/default-grid-settings';
import { OdinDataGridRowTemplate } from '../../dynamic-page/odin-data-grid-row-template.component';
import {
  getColumnSortPath,
  linkedRecordColumnValuePaths,
  linkedRecordsCellRenderer,
  linkedRecordsSortFunction,
  transformLinkedRecordDataWithKeyTypeId,
  transformRecordLinkColumns,
} from '../linked-records/linked-records-functions';
import MobileLinkedBaseRecords from '../linked-records/linked-records-mobile-base.component';

export interface ReverseLinkedRecordsProps extends DynamicFieldComponentProps<Array<RecordResult>>, RecordLinkProps {
  isPreview?: boolean;
  formId: number;
  recordId?: number;
}

export default function ReverseLinkedRecords({
  id,
  recordId,
  label,
  value,
  recordLinkFormat,
  isPreview,
  formId,
  error,
  readOnly,
  required,
  targetModuleFormId,
}: ReverseLinkedRecordsProps) {
  const [reverseLinkedRecords, setReverseLinkedRecords] = useState<Array<RecordResult>>([]);
  const [listOfPermittedRecordIds, setListOfPermittedRecordIds] = useState<string[]>([]); //list of record ids you have permissions to access
  const [pendingPermissions, setPendingPermissions] = useState<Array<string>>([]);

  const { reverseLinkedRecordsData } = useFetchReverseLinkedRecords(
    recordId,
    Number(id),
    reverseLinkedRecords.map((record) => String(record.id))
  );

  const { activeRecordReference } = useActiveRecordRef();
  const { data: modules, isLoading: areModulesLoading } = useModulesQuery({});
  const { workflowSteps } = useFetchWorkflowSteps(formId!);
  const { formPermissions } = useFormPermissions(targetModuleFormId);

  const reverseLinkedRecordsPermissions = useMemo(() => {
    return extractLinkedRecordsPermissions(formPermissions);
  }, [formPermissions]);

  const canReadRecords = reverseLinkedRecordsPermissions?.create || reverseLinkedRecordsPermissions?.read;
  const canOnlySeeDataGrid = !reverseLinkedRecordsPermissions?.create && !reverseLinkedRecordsPermissions?.read;

  const isRowClickable = useMemo(() => {
    return !isPreview && reverseLinkedRecordsPermissions?.read;
  }, [isPreview, reverseLinkedRecordsPermissions]);

  useEffect(() => {
    if (Array.isArray(value) && (canReadRecords || canOnlySeeDataGrid)) {
      setReverseLinkedRecords(value);
      setPendingPermissions(value.map((record) => String(record.id)));
      // no recordId logic handles creating a new linked record with the parent showing as a reverse linked record
      if (!recordId) {
        const initialRecordIds = value.map((record) => String(record.id));
        const finalIdList = initialRecordIds.filter((id) => !listOfPermittedRecordIds.includes(id)); // avoid duplicated ids when you click on the data grid
        setListOfPermittedRecordIds((prev) => [...prev, ...finalIdList]);
      }
    }
  }, [value, canReadRecords, canOnlySeeDataGrid]);

  useEffect(() => {
    if (reverseLinkedRecordsData && reverseLinkedRecords) {
      const fieldReverseLinkedRecordIds = reverseLinkedRecordsData
        .filter((record) => record.canRead)
        .map((record) => String(record.id));
      const recordReverseLinkedRecordsIds = reverseLinkedRecords.map((record) => String(record.id));
      const matchingIds = recordReverseLinkedRecordsIds.filter((id) => fieldReverseLinkedRecordIds.includes(id));
      const finalIdList = matchingIds.filter((id) => !listOfPermittedRecordIds.includes(id)); // make sure the id isn't already in the list
      setListOfPermittedRecordIds((prev) => [...prev, ...finalIdList]);
      setPendingPermissions([]);
    }
  }, [reverseLinkedRecordsData]);

  const gridColumns = useMemo(() => {
    return recordLinkFormat
      ? transformRecordLinkColumns(
          recordLinkFormat,
          linkedRecordColumnValuePaths,
          getColumnSortPath,
          linkedRecordsCellRenderer
        )
      : [];
  }, [recordLinkFormat]);

  const gridSettings = useMemo<DataGridSettings>(() => {
    return {
      ...defaultLinkedRecordGridSetting,
      allowCellScrollBar: true,
      columns: gridColumns,
      autoSizeColumns: true,
      sortFunction: (data: JsonData, sorts: Array<ContinuousScrollerSort>) =>
        linkedRecordsSortFunction(data, sorts, gridColumns),
      components: {
        ...defaultLinkedRecordGridSetting.components,
        ...customLinkedRecordsComponents,
      },
    };
  }, [gridColumns]);

  const rowTemplate: RowTemplateType = OdinDataGridRowTemplate({
    modules: modules,
    activeRecordReference: activeRecordReference,
    isClickable: isRowClickable,
    listOfPermittedRecordIds: listOfPermittedRecordIds,
    pendingLinkedRecords: pendingPermissions,
  });

  const rowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem, className?: string) => {
        const backgroundColor = workflowSteps?.find(
          (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
        )?.backgroundColor;

        const appliedStyles = className || 'h-4 w-4 rounded';
        return <div className={appliedStyles} style={{ backgroundColor: backgroundColor }} />;
      },
    };
  }, [workflowSteps]);

  const canShowGrid = !areModulesLoading && (canReadRecords || canOnlySeeDataGrid) && reverseLinkedRecords.length > 0;

  const { isMobile } = useScreenSize();
  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && reverseLinkedRecords.length === 0 && <EmptyFieldLabel />}
      {canShowGrid && !isMobile && (
        <div className="my-2 flex max-h-[40rem]">
          <DataGrid
            data={transformLinkedRecordDataWithKeyTypeId(forceAssert<JsonData>(reverseLinkedRecords))}
            gridSettings={gridSettings}
            rowTemplate={rowTemplate}
            showSettings={false}
            rowIndicator={rowIndicator}
          />
        </div>
      )}
      {canShowGrid && isMobile && (
        <MobileLinkedBaseRecords
          data={transformLinkedRecordDataWithKeyTypeId(forceAssert<JsonData>(reverseLinkedRecords))}
          gridColumns={gridColumns}
          listOfPermittedRecordIds={listOfPermittedRecordIds}
          pendingPermissions={pendingPermissions}
          rowIndicator={rowIndicator}
          isRowClickable={isRowClickable}
          modules={modules}
          readOnly={readOnly}
        />
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}
